import { Card, IconTile } from "@design";
import { Bot, Sparkle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { SpaceHubResponse } from "@/api";
import { SectionEmpty } from "@/components/section";
import { Text } from "@/components/Text";
import { NoAccessNote, OnlyYouBadge } from "./ItemNotes";
import { SpaceSection } from "./SpaceSection";
import { newSpaceChatPath } from "./spaceChat";

/** Agents added to the space; clicking one starts a chat with it in the space (editors). */
export function AgentsSection({ space }: Readonly<{ space: SpaceHubResponse }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.agents" });
  const navigate = useNavigate();
  const agents = (space.resources ?? []).filter((r) => r.resourceType === "agent" && r.agentUuid);
  return (
    <SpaceSection title={t("title")} action={{ label: t("all"), onClick: () => navigate("/build/agents") }}>
      {agents.length === 0 ? (
        <SectionEmpty icon={<Bot size={18} />} hint={t("empty")} />
      ) : (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {agents.map((agent) => (
            <Card
              key={agent.uuid}
              size="sm"
              onClick={
                agent.canUse !== false ? () => navigate(newSpaceChatPath(space.uuid, agent.agentUuid)) : undefined
              }
              className="flex w-full flex-col"
            >
              <div className="flex items-start gap-2.5">
                <IconTile size="sm">
                  <Sparkle size={14} />
                </IconTile>
                <div className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <Text as="span" variant="body-sm" weight="semibold" className="truncate">
                      {agent.agentName}
                    </Text>
                    <OnlyYouBadge item={agent} />
                  </span>
                  {agent.addedByName && (
                    <Text variant="caption" tone="tertiary" className="mt-1">
                      {t("addedBy", { name: agent.addedByName })}
                    </Text>
                  )}
                  <NoAccessNote item={agent} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </SpaceSection>
  );
}
